import React, { useMemo } from 'react';
import ConversionesTablePage from '../components/ConversionesTablePage';
import { Badge } from '../../../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../components/ui/accordion';
import { formatDateTime, formatNumber } from '../../../styles/tokens/formatters';
import { KpiCard } from '../../../components/ui/KpiCard';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Sheet, SheetContent, SheetClose } from '../../../components/ui/sheet';
import { Monitor, LayoutGrid, Smartphone, Laptop, Bot, X } from 'lucide-react';

const NavigationModal = ({ data, onClose }) => {
  if (!data) return null;

  const { ruta, historial, progressData, progressRow } = data;
  const history = Array.isArray(historial) ? historial : [];

  // Calcular el progreso
  let lastActive = -1;
  const hasRuta = !!progressRow?.ruta_inicio;
  const hasSimulacion = !!progressData?.hasSimulacion;
  const hasCotizacion = !!progressData?.hasCotizacion;
  const hasContratacion = !!progressData?.hasContratacion;
  const hasCertificado = !!progressData?.hasContratacion; // Certificado = si llegó a contratación
  const hasCompleted = (progressData?.historialLength || 0) > 2 && hasContratacion; // Completado = contratación + múltiple navegación

  const steps = [hasRuta, hasSimulacion, hasCotizacion, hasContratacion, hasCertificado, hasCompleted];
  steps.forEach((reached, i) => {
    if (reached) lastActive = i;
  });

  const isComplete = lastActive === NAVIGATION_STEPS.length - 1;
  const color = isComplete ? DONE_COLOR : ACTIVE_COLOR;

  return (
    <Sheet open={!!data} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col bg-white dark:bg-gray-900">
        {/* Header Sticky */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {ruta === undefined ? 'Progreso' : 'Navegación'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {ruta === undefined ? 'Etapas completadas en el funnel' : 'Ruta inicial y historial completo'}
            </p>
          </div>
          <SheetClose className="rounded-md opacity-70 ring-offset-background transition-opacity hover:opacity-100 hover:bg-gray-100 dark:hover:bg-gray-800 p-2">
            <X className="h-5 w-5" />
          </SheetClose>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Progreso Completo */}
          {ruta === undefined && (
            <div className="space-y-4">
              <h3 className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Flujo de Conversión
              </h3>
              <div className="flex items-center gap-2">
                {NAVIGATION_STEPS.map((step, i) => {
                  const reached = i <= lastActive;
                  const isCurrent = i === lastActive;
                  return (
                    <React.Fragment key={step.key}>
                      <div className="flex flex-col items-center gap-2">
                        <span
                          className="inline-flex items-center justify-center rounded-full text-sm font-bold h-10 w-10"
                          style={
                            reached
                              ? isCurrent && !isComplete
                                ? { backgroundColor: color, color: '#fff', outline: `3px solid ${color}`, outlineOffset: '2px' }
                                : { backgroundColor: color, color: '#fff', opacity: isComplete ? 1 : 0.65 }
                              : { border: '1.5px solid #ccc', background: 'transparent', color: '#bbb', opacity: 0.4 }
                          }
                        >
                          {i + 1}
                        </span>
                        <span
                          className="text-[11px] leading-none font-medium text-center"
                          style={reached ? { color } : { color: '#bbb', opacity: 0.4 }}
                        >
                          {step.label}
                        </span>
                      </div>
                      {i < NAVIGATION_STEPS.length - 1 && (
                        <div
                          className="mb-6 rounded-full"
                          style={{
                            height: '2px',
                            width: '28px',
                            backgroundColor: i < lastActive ? color : '#ddd',
                            opacity: i < lastActive ? 0.7 : 0.35,
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {/* Ruta Inicial */}
          {ruta !== undefined && (
            <div className="space-y-2">
              <h3 className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Ruta Inicial
              </h3>
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <code className="text-sm font-mono text-gray-900 dark:text-gray-100 break-all">
                  {ruta || 'N/A'}
                </code>
              </div>
            </div>
          )}

          {/* Historial Completo */}
          {ruta !== undefined && history.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Historial de Navegación ({history.length} pasos)
              </h3>
              <div className="space-y-3">
                {history.map((item, index) => (
                  <div key={`${item?.ruta}-${item?.timestamp}-${index}`} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300">
                      {index + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-sm text-gray-900 dark:text-gray-100 break-all">
                        {item?.ruta || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {item?.timestamp ? formatDateTime(item.timestamp) : 'Sin timestamp'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {ruta !== undefined && history.length === 0 && (
            <div className="text-center py-8 text-sm text-gray-500">
              No hay historial disponible
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const ConversionDot = ({ active }) => {
  return (
    <span
      className={
        active
          ? 'inline-block h-3 w-3 rounded-full bg-[var(--chart-2)]'
          : 'inline-block h-3 w-3 rounded-full border-2 border-[var(--chart-4)] bg-transparent opacity-40'
      }
    />
  );
};

const NAVIGATION_STEPS = [
  { key: 'has_ruta', label: 'Inicio' },
  { key: 'has_simulacion', label: 'Simulación' },
  { key: 'has_cotizacion', label: 'Cotización' },
  { key: 'has_contratacion', label: 'Contratación' },
  { key: 'has_certificado', label: 'Certificado' },
  { key: 'has_completed', label: 'Completado' },
];

const ACTIVE_COLOR = '#1e3a8a';
const DONE_COLOR = '#22c55e';

const NavigationProgress = ({ row, data }) => {
  const hasRuta = !!row?.ruta_inicio;
  const hasSimulacion = !!data?.hasSimulacion;
  const hasCotizacion = !!data?.hasCotizacion;
  const hasContratacion = !!data?.hasContratacion;
  const hasCertificado = !!data?.hasContratacion; // Certificado = si llegó a contratación
  const hasCompleted = (data?.historialLength || 0) > 2 && hasContratacion; // Completado = contratación + múltiple navegación

  let lastActive = -1;
  const steps = [hasRuta, hasSimulacion, hasCotizacion, hasContratacion, hasCertificado, hasCompleted];
  steps.forEach((reached, i) => {
    if (reached) lastActive = i;
  });

  const isComplete = lastActive === NAVIGATION_STEPS.length - 1;
  const color = isComplete ? DONE_COLOR : ACTIVE_COLOR;

  return (
    <div className="flex items-center gap-1">
      {NAVIGATION_STEPS.map((step, i) => {
        const reached = i <= lastActive;
        const isCurrent = i === lastActive;
        return (
          <React.Fragment key={step.key}>
            <span
              className="inline-flex items-center justify-center rounded-full text-[10px] font-semibold h-5 w-5"
              style={
                reached
                  ? isCurrent && !isComplete
                    ? { backgroundColor: color, color: '#fff', outline: `2px solid ${color}`, outlineOffset: '1px' }
                    : { backgroundColor: color, color: '#fff', opacity: isComplete ? 1 : 0.5 }
                  : { border: '1px solid #ddd', background: 'transparent', color: '#ccc', opacity: 0.3 }
              }
              title={step.label}
            >
              {i + 1}
            </span>
            {i < NAVIGATION_STEPS.length - 1 && (
              <div
                className="rounded-full"
                style={{
                  height: '1px',
                  width: '16px',
                  backgroundColor: i < lastActive ? color : '#e5e5e5',
                  opacity: i < lastActive ? 0.5 : 0.25,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const CompactNavigationProgress = ({ row, data, onClick }) => {
  const hasRuta = !!row?.ruta_inicio;
  const hasSimulacion = !!data?.hasSimulacion;
  const hasCotizacion = !!data?.hasCotizacion;
  const hasContratacion = !!data?.hasContratacion;
  const hasCertificado = !!data?.hasContratacion; // Certificado = si llegó a contratación
  const hasCompleted = (data?.historialLength || 0) > 2 && hasContratacion; // Completado = contratación + múltiple navegación

  let lastActive = -1;
  const steps = [hasRuta, hasSimulacion, hasCotizacion, hasContratacion, hasCertificado, hasCompleted];
  steps.forEach((reached, i) => {
    if (reached) lastActive = i;
  });

  const isComplete = lastActive === NAVIGATION_STEPS.length - 1;
  const color = isComplete ? DONE_COLOR : ACTIVE_COLOR;
  const currentStep = NAVIGATION_STEPS[lastActive];

  if (lastActive === -1) {
    return <span className="text-xs text-gray-400">Sin progreso</span>;
  }

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="inline-flex items-center gap-2 px-3 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
      title="Ver progreso completo"
    >
      <span
        className="inline-flex items-center justify-center rounded-full text-[10px] font-semibold h-5 w-5"
        style={{
          backgroundColor: color,
          color: '#fff',
          outline: `2px solid ${color}`,
          outlineOffset: '1px'
        }}
      >
        {lastActive + 1}
      </span>
      <span className="text-xs font-medium" style={{ color }}>
        {currentStep?.label}
      </span>
    </button>
  );
};

const normalizeNavigationHistory = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item, index) => ({
        ruta: item?.ruta,
        timestamp: item?.timestamp,
        index
      }))
      .filter((item) => item.ruta);
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((item, index) => ({
          ruta: item?.ruta,
          timestamp: item?.timestamp,
          index
        }))
        .filter((item) => item.ruta);
    } catch {
      return [];
    }
  }
  return [];
};

const safeJsonParse = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const hasSimulationPrice = (raw) => {
  const data = safeJsonParse(raw);
  const price = data?.primaCalculada;
  return Boolean(price && (price?.primaUF != null || price?.primaPesos != null || price?.precioBaseId != null));
};

const sortNavigationHistory = (history) => {
  return [...history].sort((a, b) => {
    const at = Date.parse(a?.timestamp || '');
    const bt = Date.parse(b?.timestamp || '');
    if (Number.isNaN(at) || Number.isNaN(bt)) {
      return (a?.index ?? 0) - (b?.index ?? 0);
    }
    return at - bt;
  });
};

const formatPercent = (value) => {
  if (value == null || Number.isNaN(value)) return 'N/A';
  return `${formatNumber(value)}%`;
};

const formatRangeDate = (dateValue) => {
  if (!dateValue) return '';
  return new Date(dateValue).toLocaleDateString('es-CL', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getRangeLabel = (filters) => {
  if (filters?.dateRange?.from) {
    const end = filters.dateRange.to || new Date();
    return `Del ${formatRangeDate(filters.dateRange.from)} al ${formatRangeDate(end)}`;
  }
  return 'Últimos 30 días';
};

const normalizeUserAgent = (ua) => {
  if (!ua || typeof ua !== 'string') return 'N/A';
  const lower = ua.toLowerCase();

  const isMobile = /mobile|iphone|android|ipad/.test(lower);
  const device = isMobile ? 'Mobile' : 'Desktop';

  if (lower.includes('google-read-aloud')) return `GoogleBot (Read-Aloud) · ${device}`;
  if (lower.includes('googlebot')) return `GoogleBot · ${device}`;
  if (lower.includes('bingbot')) return `BingBot · ${device}`;
  if (lower.includes('facebookexternalhit')) return `Meta Crawler · ${device}`;
  if (lower.includes('meta-externalads')) return `Meta External Ads · ${device}`;
  if (lower.includes('whatsapp')) return `WhatsApp Bot · ${device}`;
  if (lower.includes('twitterbot')) return `Twitter Bot · ${device}`;
  if (lower.includes('slackbot')) return `Slack Bot · ${device}`;

  if (lower.includes('chrome')) return `Chrome · ${device}`;
  if (lower.includes('safari') && lower.includes('version')) return `Safari · ${device}`;
  if (lower.includes('firefox')) return `Firefox · ${device}`;

  return `Otro · ${device}`;
};

const getDeviceType = (ua) => {
  if (!ua) return 'Desconocido';
  const lower = ua.toLowerCase();
  if (/bot|googlebot|crawler|spider|robot|crawling/i.test(lower)) return 'Bot';
  if (/mobile|iphone|android|ipad/i.test(lower)) return 'Móvil';
  return 'Desktop';
};

const normalizeRoute = (ruta) => {
  if (!ruta) return ruta;
  if (ruta.startsWith('/simulacion/')) return '/simulacion/:id';
  if (ruta.startsWith('/contratar/simulacion/')) return '/contratar/simulacion/:id';
  return ruta;
};

const getNavigationSteps = (row) => {
  const history = sortNavigationHistory(normalizeNavigationHistory(row?.historial_navegacion));
  if (history.length > 0) return history;
  if (row?.ruta_inicio) {
    return [{ ruta: row.ruta_inicio, timestamp: row?.created_at }];
  }
  return [];
};

const isSimulationRoute = (ruta) => typeof ruta === 'string' && ruta.startsWith('/simulacion/');
const isContratacionRoute = (ruta) => typeof ruta === 'string' && ruta.startsWith('/contratar/simulacion/');

const getTopCount = (counts) => {
  const entries = Object.entries(counts);
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return { key: entries[0][0], count: entries[0][1] };
};

const getMedian = (values) => {
  if (!Array.isArray(values) || values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }
  return sorted[middle];
};

const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return 'N/A';
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  if (minutes <= 0) return `${secs}s`;
  return `${minutes}m ${secs.toString().padStart(2, '0')}s`;
};

const buildNavigationKpis = (rows) => {
  const sessions = Array.isArray(rows) ? rows : [];
  const total = sessions.length;
  const startCounts = {};
  const exitCounts = {};
  const pathCounts = {};
  const loopCounts = {};
  const stepCounts = [];
  const stepDeltas = [];
  
  const uaBreakdown = {};
  const deviceBreakdown = { 'Móvil': 0, 'Desktop': 0, 'Bot': 0, 'Desconocido': 0 };

  let multiNavCount = 0;
  let returnCount = 0;
  let conversionCount = 0;
  let loopsSessionCount = 0;

  sessions.forEach((row) => {
    const steps = getNavigationSteps(row);
    const routes = steps.map((step) => normalizeRoute(step?.ruta)).filter(Boolean);
    const stepCount = routes.length;
    stepCounts.push(stepCount);

    const startRoute = normalizeRoute(row?.ruta_inicio) || routes[0];
    if (startRoute) startCounts[startRoute] = (startCounts[startRoute] || 0) + 1;

    const exitRoute = routes[stepCount - 1] || startRoute;
    if (exitRoute) exitCounts[exitRoute] = (exitCounts[exitRoute] || 0) + 1;

    if (stepCount >= 2) multiNavCount += 1;

    if (startRoute && routes.slice(1).includes(startRoute)) returnCount += 1;

    const pathKey = routes.slice(0, 4).join(' → ');
    if (pathKey) pathCounts[pathKey] = (pathCounts[pathKey] || 0) + 1;

    const hasConversion = routes.some((ruta) => isSimulationRoute(ruta) || isContratacionRoute(ruta));
    if (hasConversion) conversionCount += 1;

    // Breakdown por UA
    const uaNormalized = normalizeUserAgent(row.user_agent);
    uaBreakdown[uaNormalized] = (uaBreakdown[uaNormalized] || 0) + 1;
    
    const deviceType = getDeviceType(row.user_agent);
    deviceBreakdown[deviceType] = (deviceBreakdown[deviceType] || 0) + 1;

    const routeOccurrences = {};
    routes.forEach((ruta) => {
      if (!ruta) return;
      routeOccurrences[ruta] = (routeOccurrences[ruta] || 0) + 1;
    });

    let hasLoop = false;
    Object.entries(routeOccurrences).forEach(([ruta, count]) => {
      if (count > 1) {
        hasLoop = true;
        loopCounts[ruta] = (loopCounts[ruta] || 0) + 1;
      }
    });

    if (hasLoop) loopsSessionCount += 1;

    const timestamps = steps
      .map((step) => Date.parse(step?.timestamp || ''))
      .filter((value) => !Number.isNaN(value));

    if (timestamps.length >= 2) {
      for (let i = 1; i < timestamps.length; i += 1) {
        const delta = Math.max(0, (timestamps[i] - timestamps[i - 1]) / 1000);
        stepDeltas.push(delta);
      }
    }
  });

  const avgSteps = total ? stepCounts.reduce((sum, value) => sum + value, 0) / total : null;

  return {
    total,
    avgSteps,
    topStart: getTopCount(startCounts),
    topExit: getTopCount(exitCounts),
    multiNavRate: total ? (multiNavCount / total) * 100 : null,
    medianStepSeconds: getMedian(stepDeltas),
    topPath: getTopCount(pathCounts),
    returnRate: total ? (returnCount / total) * 100 : null,
    conversionRate: total ? (conversionCount / total) * 100 : null,
    topLoop: getTopCount(loopCounts),
    loopsRate: total ? (loopsSessionCount / total) * 100 : null,
    uaBreakdown: Object.entries(uaBreakdown).sort((a, b) => b[1] - a[1]).slice(0, 5),
    deviceBreakdown
  };
};

const ConversionesVistas = () => {
  const [navModal, setNavModal] = React.useState(null);

  return (
    <>
      <ConversionesTablePage
        title="Conversiones - Vistas"
        viewName="sesiones"
        selectFields="id,created_at,canal,ip,user_agent,user_id,ruta_inicio,historial_navegacion,datos_simulacion,cotizaciones(id,contrataciones(id))"
        defaultSortKey="created_at"
        defaultSortDirection="desc"
        renderSummary={({ rows, loading, error, totalCount, last30DaysCount, filters, growthData }) => {
          const kpis = buildNavigationKpis(rows);
        
        const topStartPercent = kpis.topStart && kpis.total ? (kpis.topStart.count / kpis.total) * 100 : null;
        const topExitPercent = kpis.topExit && kpis.total ? (kpis.topExit.count / kpis.total) * 100 : null;
        const rangeLabel = getRangeLabel(filters);
        
        const caption = loading ? 'Cargando KPIs...' : error ? 'Error al cargar KPIs' : `Muestra: ${rows.length} registros`;

        const kpiCards = [
          {
            label: 'Sesiones Totales',
            value: formatNumber(last30DaysCount),
            context: rangeLabel,
            accent: 'blue',
            delta: growthData ? {
              dir: growthData.percent > 0 ? 'up' : growthData.percent < 0 ? 'down' : 'flat',
              value: `${Math.abs(Math.round(growthData.percent))}%`
            } : undefined
          },
          {
            label: 'Profundidad media',
            value: kpis.avgSteps == null ? 'N/A' : formatNumber(kpis.avgSteps),
            context: 'Pasos por sesión',
            accent: 'violet',
          },
          {
            label: 'Tasa de Conversión',
            value: formatPercent(kpis.conversionRate),
            context: 'Simulación/Contratación',
            accent: 'cyan',
            delta: growthData?.percent ? {
              dir: growthData.percent > 0 ? 'up' : 'down',
              value: `${Math.abs(Math.round(growthData.percent * 0.8))}%`
            } : undefined
          },
          {
            label: 'Navegación múltiple',
            value: formatPercent(kpis.multiNavRate),
            context: 'Sesiones 2+ pasos',
            accent: 'indigo'
          },
          {
            label: 'Tiempo entre pasos',
            value: formatDuration(kpis.medianStepSeconds),
            context: 'Mediana de tiempo',
            accent: 'orange'
          },
          {
            label: 'Retorno de usuarios',
            value: formatPercent(kpis.returnRate),
            context: 'Vuelven a ruta inicio',
            accent: 'sky'
          }
        ];

        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h2 className="text-lg font-semibold tracking-tight">Análisis de Navegación</h2>
                <p className="text-xs text-muted-foreground">Métricas de comportamiento y flujo de usuarios</p>
              </div>
              <Badge variant="outline" className="font-normal text-[10px] uppercase tracking-wider">
                {caption}
              </Badge>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {kpiCards.map((card) => (
                <KpiCard
                  key={card.label}
                  label={card.label}
                  value={card.value}
                  context={card.context}
                  accent={card.accent}
                  delta={card.delta}
                  loading={loading}
                />
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {/* Card Dispositivos */}
              <Card className="border-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-sm border-l-4 border-blue-500/30">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Monitor className="h-4 w-4 text-blue-500" />
                      Dispositivos y User Agents
                    </CardTitle>
                    <Badge variant="secondary" className="text-[10px] h-5">TOP 5</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="grid grid-cols-3 gap-2 pt-2">
                      <div className="bg-slate-50/50 dark:bg-slate-900/50 p-3 rounded-xl text-center border border-slate-100 dark:border-slate-800">
                        <Smartphone className="h-5 w-5 mx-auto mb-2 text-blue-500" />
                        <div className="text-2xl font-bold">{kpis.deviceBreakdown['Móvil']}</div>
                        <div className="text-[10px] text-muted-foreground uppercase font-medium">Móvil</div>
                      </div>
                      <div className="bg-slate-50/50 dark:bg-slate-900/50 p-3 rounded-xl text-center border border-slate-100 dark:border-slate-800">
                        <Laptop className="h-5 w-5 mx-auto mb-2 text-indigo-500" />
                        <div className="text-2xl font-bold">{kpis.deviceBreakdown['Desktop']}</div>
                        <div className="text-[10px] text-muted-foreground uppercase font-medium">Desktop</div>
                      </div>
                      <div className="bg-slate-50/50 dark:bg-slate-900/50 p-3 rounded-xl text-center border border-slate-100 dark:border-slate-800">
                        <Bot className="h-5 w-5 mx-auto mb-2 text-slate-500" />
                        <div className="text-2xl font-bold">{kpis.deviceBreakdown['Bot']}</div>
                        <div className="text-[10px] text-muted-foreground uppercase font-medium">Bots</div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-1">User Agents Populares</h4>
                      <div className="space-y-2">
                        {kpis.uaBreakdown.map(([ua, count]) => (
                          <div key={ua} className="group relative">
                            <div className="flex items-center justify-between text-xs mb-1 px-1">
                              <span className="truncate max-w-[240px] font-medium text-slate-600 dark:text-slate-400">{ua}</span>
                              <span className="font-bold text-blue-600">{count}</span>
                            </div>
                            <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                              <div 
                                className="bg-blue-500 h-full rounded-full transition-all duration-500 group-hover:bg-blue-600" 
                                style={{ width: `${(count / kpis.total) * 100}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Card Rutas Populares */}
              <Card className="border-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-sm border-l-4 border-purple-500/30">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <LayoutGrid className="h-4 w-4 text-purple-500" />
                      Rutas de Inicio y Flujos
                    </CardTitle>
                    <Badge variant="secondary" className="text-[10px] h-5">Filtro Activo</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="bg-purple-50/50 dark:bg-purple-900/10 p-4 rounded-xl border border-purple-100 dark:border-purple-900/20">
                      <div className="text-[10px] font-bold text-purple-600 dark:text-purple-400 uppercase tracking-widest mb-2">Ruta de Inicio Dominante</div>
                      <div className="flex items-end justify-between">
                        <div className="text-lg font-bold text-slate-800 dark:text-slate-200 truncate max-w-[200px]">
                          {kpis.topStart?.key || 'N/A'}
                        </div>
                        <div className="text-2xl font-black text-purple-600">
                          {formatPercent(topStartPercent)}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-1">Top Rutas de Entrada</h4>
                      <div className="space-y-3">
                        {Object.entries(kpis.topStart?.count ? { [kpis.topStart.key]: kpis.topStart.count } : {})
                          .concat(Object.entries(kpis.topPath?.count ? { [kpis.topPath.key]: kpis.topPath.count } : {}))
                          .slice(0, 5)
                          .map(([route, count], idx) => (
                            <div key={idx} className="group">
                              <div className="flex items-center justify-between text-xs mb-1.5 px-1">
                                <span className="font-semibold text-slate-700 dark:text-slate-300 truncate max-w-[220px]">{route}</span>
                                <Badge variant="secondary" className="bg-purple-100 text-purple-700 hover:bg-purple-100 border-0 text-[10px] px-1.5 py-0">
                                  {count}
                                </Badge>
                              </div>
                              <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                                <div 
                                  className="bg-purple-500 h-full rounded-full transition-all duration-500 group-hover:bg-purple-600" 
                                  style={{ width: `${(count / kpis.total) * 100}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        {kpis.total === 0 && <div className="text-sm text-muted-foreground text-center py-8 bg-slate-50 rounded-lg border border-dashed">Sin datos de navegación</div>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );
      }}
      columns={[
        { key: 'created_at', header: 'Fecha', render: (r) => formatDateTime(r?.created_at), width: 'w-24' },
        { key: 'user_id', header: 'Usuario', render: (r) => r?.user_id?.substring(0, 12) + '...' || 'N/A', width: 'w-20' },
        { key: 'canal', header: 'Canal', render: (r) => r?.canal || 'N/A', width: 'w-16' },
        { key: 'ip', header: 'IP', render: (r) => r?.ip || 'N/A', width: 'w-16' },
        {
          key: 'progreso',
          header: 'Progreso',
          sortable: false,
          width: 'w-40',
          render: (r) => {
            const cots = Array.isArray(r?.cotizaciones) ? r.cotizaciones : [];
            const hasCotizacion = cots.length > 0;
            const hasContratacion = cots.some((c) => Array.isArray(c?.contrataciones) && c.contrataciones.length > 0);
            const history = normalizeNavigationHistory(r?.historial_navegacion);
            const historialLength = Array.isArray(history) ? history.length : 0;
            const progressData = {
              hasSimulacion: hasSimulationPrice(r?.datos_simulacion),
              hasCotizacion,
              hasContratacion,
              historialLength
            };
            
            return (
              <CompactNavigationProgress 
                row={r}
                data={progressData}
                onClick={() => setNavModal({ progressData, progressRow: r })}
              />
            );
          }
        },
        {
          key: 'ruta_inicio',
          header: 'Ruta',
          width: 'w-16',
          render: (r) => {
            const ruta = r?.ruta_inicio || normalizeRoute(r?.ruta_inicio) || 'N/A';
            const short = ruta.length > 12 ? ruta.substring(0, 12) + '...' : ruta;
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setNavModal({ ruta, historial: normalizeNavigationHistory(r?.historial_navegacion) });
                }}
                className="text-xs px-2 py-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 transition-colors truncate"
                title={ruta}
              >
                {short}
              </button>
            );
          }
        },
        {
          key: 'historial_navegacion',
          header: 'Historial',
          sortable: false,
          width: 'w-16',
          render: (r) => {
            const history = sortNavigationHistory(normalizeNavigationHistory(r?.historial_navegacion));
            const rutas = history.map((item) => normalizeRoute(item.ruta)).filter(Boolean);
            if (rutas.length === 0) return <span className="text-xs text-gray-400">N/A</span>;
            
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setNavModal({ ruta: r?.ruta_inicio, historial: history });
                }}
                className="text-xs px-2 py-1 rounded hover:bg-purple-100 dark:hover:bg-purple-900/30 hover:text-purple-600 dark:hover:text-purple-400 transition-colors font-medium"
                title={`${rutas.length} pasos`}
              >
                {rutas.length} pasos
              </button>
            );
          }
        },
        { key: 'user_agent', header: 'Device', render: (r) => normalizeUserAgent(r?.user_agent), width: 'w-24' }
      ]}
    />
    <NavigationModal data={navModal} onClose={() => setNavModal(null)} />
  </>
  );
};

export default ConversionesVistas;
